import {isFeatureEnabled} from 'feature-flags';

import {useCallback} from 'react';

import {aiAssistantContextStore} from '@dms/ai-assistant';
import {featureFlags} from '@dms/feature-flags';

import {useOnMount} from 'shared/web';

import {EagGrid, type EagGridProps, type GridStateChange} from 'features/eag-grid';

export type DmsDataGridProps<T extends readonly string[] = readonly string[]> = Omit<
  EagGridProps<T>,
  'predefinedColumns'
> & {
  predefinedColumns?: T;
};

const EMPTY_COLUMNS: readonly string[] = [];

function DmsDataGridInner<T extends readonly string[] = readonly string[]>(
  props: DmsDataGridProps<T>
) {
  // Feeds the store with the active grid's filters + sort (gridCode from props). useCallback is
  // required (not speculative): EagGrid puts onGridStateChange in a useMemo'd context value's deps,
  // so an unstable identity rebuilds that context and re-renders all grid consumers every render.
  const handleGridStateChange = useCallback(
    (state: GridStateChange) =>
      aiAssistantContextStore.patchContext({
        activeGrid: {gridCode: props.gridCode, filters: state.filters, sort: state.sort},
      }),
    [props.gridCode]
  );

  useOnMount(undefined, () => {
    if (aiAssistantContextStore.getContext().activeGrid?.gridCode === props.gridCode) {
      aiAssistantContextStore.patchContext({activeGrid: undefined});
    }
  });

  return (
    <EagGrid
      {...props}
      predefinedColumns={props.predefinedColumns ?? EMPTY_COLUMNS}
      // After the spread: an explicitly passed value wins, but a forwarded
      // `undefined` falls back to the feature flag instead of disabling.
      columnsPanelScrollToEnabled={
        props.columnsPanelScrollToEnabled ??
        isFeatureEnabled(featureFlags.EAG_GRID_EXPERIMENTAL_COLUMNS_PANEL_SCROLL_TO)
      }
      drawerModeEnabled={
        props.drawerModeEnabled ?? isFeatureEnabled(featureFlags.EAG_GRID_EXPERIMENTAL_DRAWER_MODE)
      }
      filterByValueEnabled={
        props.filterByValueEnabled ??
        isFeatureEnabled(featureFlags.EAG_GRID_EXPERIMENTAL_FILTER_BY_VALUE)
      }
      onGridStateChange={handleGridStateChange}
    />
  );
}

export function DmsDataGrid<T extends readonly string[] = readonly string[]>(
  props: DmsDataGridProps<T>
) {
  return <DmsDataGridInner key={props.gridCode} {...props} />;
}
